import type { ReactNode } from 'react';
import type { AssignKind } from '../../../types/project-assign.js';

/**
 * 種別ごとの配色。要件定義は薄い緑、開発は黄色。
 *
 * `var(--color-*)` ではなく固定色にしているのは、この 2 色が**種別の識別子**で、テーマの
 * 意味色（success / warning）とは役割が違うため。success/warning を使うと「成功/警告」と読まれる。
 * light / dark どちらでも読めるよう、背景は彩度を落とし文字は濃色に固定する。
 * @graph-connects none
 */
export const ASSIGN_KIND_TONES: Record<
  AssignKind,
  { label: string; activeBg: string; activeText: string; pillBg: string; pillText: string }
> = {
  requirement: {
    label: '要件定義アサイン',
    activeBg: '#A7E8C0',
    activeText: '#0B3D22',
    pillBg: '#D9F5E4',
    pillText: '#0B3D22',
  },
  development: {
    label: '開発アサイン',
    activeBg: '#F6D96B',
    activeText: '#422006',
    pillBg: '#FBEFC2',
    pillText: '#422006',
  },
};

/** 種別の並び（トグルの左右の順序）。 @graph-connects none */
const KIND_ORDER: readonly AssignKind[] = ['requirement', 'development'];

/**
 * アサイン種別の pill トグル。
 *
 * `role="tablist"` にしているのは、切り替えで下の入力欄の意味（列数）が変わるため
 * （単なるラジオではなくビューの切り替え）。キーボードは Tab + Enter/Space で操作できる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 案件アサインの種別（要件定義=薄緑 / 開発=黄）を pill 型トグルで切り替える
 * @graph-connects none
 */
export function AssignKindToggle({
  value,
  onChange,
  disabled = false,
}: {
  value: AssignKind;
  onChange: (kind: AssignKind) => void;
  disabled?: boolean;
}): ReactNode {
  return (
    <div
      role="tablist"
      aria-label="アサイン種別"
      className="inline-flex gap-1 rounded-full bg-[var(--color-bg-elevated)] p-1"
    >
      {KIND_ORDER.map((kind) => {
        const tone = ASSIGN_KIND_TONES[kind];
        const active = kind === value;
        return (
          <button
            key={kind}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => {
              onChange(kind);
            }}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors disabled:opacity-50 ${
              active ? '' : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
            }`}
            style={active ? { backgroundColor: tone.activeBg, color: tone.activeText } : undefined}
          >
            {tone.label}
          </button>
        );
      })}
    </div>
  );
}
